import type { WebBoxInputDto } from "@/modules/box/services/boxDto"
import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import {
  convertAddressFromFormik,
  type AddressFormikType,
  type AddressInputKey,
} from "@modules/address/addressFormikType"
import { word } from "@modules/core/utils/i18n"
import { type LocationDto } from "@modules/types/locationTypes"
import parsePhoneNumber, { type CountryCode } from "libphonenumber-js"
import * as Yup from "yup"

export type BoxInputKey =
  | "name"
  | "email"
  | "phoneNumberCountryCode"
  | "phoneNumber"
  | "siren"
  | "description"
  | "logo"
  | "cover"
  | "boxPhotos"
  | "location"
  | "zoneId"
  | AddressInputKey

export type BoxFormikType = {
  name: string
  description: string
  phoneNumberCountryCode: string
  phoneNumber: string
  email: string
  siren: string
  logo: MediaItemFormType | null
  cover: MediaItemFormType | null
  boxPhotos: MediaItemFormType[] | null
  location: LocationDto | null
  zoneId: string | null
} & AddressFormikType

export const convertBoxFormFormikToInputDto = (formik: BoxFormikType): WebBoxInputDto => {
  return {
    name: formik.name,
    address: convertAddressFromFormik(formik),
    phoneNumberWithPrefix: {
      number: formik.phoneNumber,
      countryCode: formik.phoneNumberCountryCode,
    },
    email: formik.email,
    logoKey: formik.logo?.key ?? null,
    coverKey: formik.cover?.key ?? null,
    imageKeys: formik.boxPhotos?.map((photo) => photo.key ?? null).filter((key) => key !== null) ?? null,
    siren: formik.siren,
    description: formik.description,
    location: formik.location ?? null,
    zoneId: formik.zoneId ?? null,
  }
}

const firstPageValidation = () =>
  Yup.object({
    name: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .required(word("global.form.required")),
  })

const secondPageValidation = (timezones: string[]) =>
  Yup.object({
    address: Yup.string()
      .max(255, word("form.maximum.characters", { count: 255 }))
      .required(word("global.form.required")),
    additionalAddress: Yup.string().max(255, word("form.maximum.characters", { count: 255 })),
    postalCode: Yup.string()
      .required(word("global.form.required"))
      .max(10, word("form.maximum.characters", { count: 10 })),
    city: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .required(word("global.form.required")),
    country: Yup.string()
      .max(255, word("form.maximum.characters", { count: 255 }))
      .required(word("global.form.required")),
    zoneId: Yup.string().required(word("form.required.timezones")).oneOf(timezones, word("form.required.timezones")),
  })

const thirdPageValidation = () =>
  Yup.object({
    email: Yup.string()
      .required(word("global.form.required"))
      .max(64, word("form.maximum.characters", { count: 64 }))
      .email(word("box.form.email.error")),
    phoneNumber: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .test(
        "phoneNumber",
        word("box.form.phone.error"),
        (value, context: { parent: { phoneNumberCountryCode: CountryCode } }) => {
          if (value) {
            const parse = parsePhoneNumber(value, context.parent.phoneNumberCountryCode)
            return !!parse?.isValid()
          }
          return false // if no value, return false
        },
      ),
    phoneNumberCountryCode: Yup.string().max(2, word("form.maximum.characters", { count: 2 })),
  })

const fourthPageValidation = () =>
  Yup.object({
    logo: Yup.mixed().required(word("box.form.logo.error")),
    cover: Yup.mixed().required(word("box.form.cover.error")),
  })

const fifthPageValidation = () => Yup.object({ description: Yup.string().required(word("global.form.required")) })

const sixthPageValidation = () =>
  Yup.object({
    siren: Yup.string()
      .max(16, word("form.maximum.characters", { count: 16 }))
      .required(word("global.form.required")),
  })

export function getValidationSchema(pageNumber?: number) {
  const timezones = Intl.supportedValuesOf("timeZone")

  switch (pageNumber) {
    case 1:
      return firstPageValidation()
    case 2:
      return secondPageValidation(timezones)
    case 3:
      return thirdPageValidation()
    case 4:
      return fourthPageValidation()
    case 5:
      return fifthPageValidation()
    case 6:
      return sixthPageValidation()
    default:
      return firstPageValidation()
        .concat(secondPageValidation(timezones))
        .concat(thirdPageValidation())
        .concat(fourthPageValidation())
        .concat(fifthPageValidation())
        .concat(sixthPageValidation())
  }
}
